import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import {
  DeviceFileListStatus,
  DeviceFileStatus,
  fetchEncoderConfigurationsUsage,
  MotionMasterMessage,
  parseParameterValue,
} from 'motion-master-client';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, from, of, zip } from 'rxjs';
import { catchError, exhaustMap, filter, first, map, mergeMap, tap, timeout, withLatestFrom } from 'rxjs/operators';

import { getDeviceFileRequest } from './device-file.actions';
import {
  getDeviceParameters,
  getDeviceParameterValuesRequest,
  loadDeviceParameterValues,
  updateDeviceParameters,
} from './device-parameter.actions';
import {
  addDevice,
  circuloEncoderConfigurationProcedureCompleted,
  writeCirculoEncoderConfigFailed,
  factoryResetRequest,
  factoryResetStatus,
  getDevicesRequest,
  getDevicesStatus,
  loadDeviceConfigurationFromFileRequest,
  loadDeviceConfigurationFromFileStatus,
  startCirculoEncoderConfigurationRequest,
  startCirculoEncoderConfigurationStatus,
  triggerCirculoRegisterReadAfterWritingEncoderConfigBin,
} from './device.actions';
import { Device } from './device.model';
import {
  clearSystemInformationStartOptions,
  getEncoderConfigurationUsageRequest,
  getEncoderConfigurationUsageStatus,
  getSystemInformationRequest,
  getSystemInformationSuccess,
  hideLoading,
  loadDockerEngineApiEnabled,
  loadOblacDrivesUpdateServiceVersion,
  openHomingMethodDescriptionModal,
  openParameterDocumentationModal,
  openQuickOpenModal,
  bulkSaveConfigurationFilesRequest,
  bulkSaveConfigurationFilesStatus,
  saveProblemReportRequest,
  saveProblemReportStatus,
  showLoading,
  systemEvent,
  updateDockerEngineApiEnabled,
  updateGlobalState,
  updateMotionMasterAlive,
  updateMotionMasterState,
  updateProblemReportSavingRunning,
} from './global.actions';
import {
  selectGlobalAppVersion,
  selectGlobalOblacDrivesUpdateServiceVersion,
  selectGlobalSystemInformation,
} from './global.selectors';

import { AppState, selectAllDevices, selectMatchingRelease } from './index';

import { allBaseParameterValuesLoaded$, mockDeviceFileOnError } from '@app/custom-operators';
import {
  circuloEncoderConfigurationCompleted,
  circuloEncoderConfigurationEnded,
  procedureStatusEnded,
} from '@app/models/status-resolve';
import { ConnectionService } from '@app/services/connection.service';
import { ElectronService } from '@app/services/electron.service';
import { GlobalService } from '@app/services/global.service';
import { ModalService } from '@app/services/modal.service';
import { makeLocalDateTimeStringForFileSave } from '@app/util';
import { environment } from '@environments/environment';

@Injectable()
export class GlobalEffects {
  private actions$ = inject(Actions);
  private cn = inject(ConnectionService);
  private globalService = inject(GlobalService);
  private electronService = inject(ElectronService);
  private modalService = inject(ModalService);
  private http = inject(HttpClient);
  private store$ = inject<Store<AppState>>(Store);
  private toastr = inject(ToastrService);

  dockerEngineApiUrl = `${window.location.protocol}//${window.location.hostname}:${environment.dockerEngineApiPort}`;

  loadSystemInformationOnMotionMasterAlive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterAlive),
      filter((action) => action.alive),
      map(() => getSystemInformationRequest({ timeout: 30000 })),
    ),
  );

  clearSystemInformationStartOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearSystemInformationStartOptions),
      withLatestFrom(this.globalService.systemInformation$),
      map(([_, systemInformation]) =>
        updateGlobalState({
          state: {
            systemInformation: {
              version: systemInformation.version,
              startOptions: { ...systemInformation.startOptions, driver: null, mac: null },
            },
          },
        }),
      ),
    ),
  );

  hideLoadingOnUpdateMotionMasterAlive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterAlive),
      filter((action) => !action.alive),
      map(() => hideLoading({ reason: 'Motion Master is as dead as a Dodo!' })),
    ),
  );

  requestLoadDockerEngineApiEnabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDockerEngineApiEnabled),
      mergeMap(() =>
        this.http.get(`${this.dockerEngineApiUrl}/info`).pipe(
          map(() => updateDockerEngineApiEnabled({ enabled: true })),
          catchError(() => of(updateDockerEngineApiEnabled({ enabled: false }))),
        ),
      ),
    ),
  );

  requestLoadOblacDrivesUpdateServiceVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOblacDrivesUpdateServiceVersion),
      mergeMap(() =>
        this.http.get(`${this.dockerEngineApiUrl}/containers/oblac-drives-update-service/json`).pipe(
          map((data: any) => {
            const oblacDrivesUpdateServiceVersion = data?.Config?.Labels.tag;
            return updateGlobalState({ state: { oblacDrivesUpdateServiceVersion } });
          }),
          catchError(() => of(updateGlobalState({ state: { oblacDrivesUpdateServiceVersion: '' } }))),
        ),
      ),
    ),
  );

  handleGetSystemInformationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSystemInformationRequest),
      withLatestFrom(this.store$),
      mergeMap(([action, state]) =>
        this.cn.client.request.getSystemInformation(action.timeout).pipe(
          map((systemInformation) => {
            const startOptions = state.global.systemInformation.startOptions ?? {};
            return getSystemInformationSuccess({
              systemInformation: {
                ...systemInformation,
                // Extend start options with properties needed for UI
                startOptions: { ...systemInformation.startOptions, ...startOptions },
              },
            });
          }),
        ),
      ),
    ),
  );

  updateMotionMasterStateOnSystemEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(systemEvent),
      map((action) => action.systemEvent.state),
      map((state) => updateMotionMasterState({ state })),
    ),
  );

  showLoadingOnMotionMasterSlavesFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterState),
      filter((action) => action.state === MotionMasterMessage.Status.SystemEvent.State.SLAVES_FOUND),
      map(() => showLoading({ text: 'initializing devices', reason: 'Motion Master has found slaves' })),
    ),
  );

  showLoadingOnMotionMasterInitializing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterState),
      filter((action) => action.state === MotionMasterMessage.Status.SystemEvent.State.INITIALIZING),
      map(() => showLoading({ text: 'initializing', reason: 'Motion Master has started to initialize' })),
    ),
  );

  hideLoadingOnMotionMasterInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterState),
      filter((action) => action.state === MotionMasterMessage.Status.SystemEvent.State.INITIALIZED),
      map(() => hideLoading({ reason: 'Motion Master has initialized' })),
    ),
  );

  showLoadingOnMotionMasterDeinitializing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterState),
      filter((action) => action.state === MotionMasterMessage.Status.SystemEvent.State.DEINITIALIZING),
      map(() => showLoading({ text: 'deinitializing', reason: 'Motion Master has started to deinitialize' })),
    ),
  );

  hideLoadingOnMotionMasterDeinitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterState),
      filter((action) => action.state === MotionMasterMessage.Status.SystemEvent.State.DEINITIALIZED),
      map(() => hideLoading({ reason: 'Motion Master has deinitialized' })),
    ),
  );

  showLoadingOnGetDevicesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDevicesRequest),
      map(() => showLoading({ text: 'loading devices' })),
    ),
  );

  hideLoadingOnGetDeviceInfoStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDevicesStatus),
      map(() => hideLoading({})),
    ),
  );

  showLoadingOnGetDeviceParameters = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceParameters),
      map(() => showLoading({ text: 'loading device parameters' })),
    ),
  );

  hideLoadingOnGetDeviceParameters$ = createEffect(() =>
    allBaseParameterValuesLoaded$(this.actions$).pipe(map(() => hideLoading({}))),
  );

  showLoadingOnLoadDeviceParameterValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceParameterValuesRequest),
      filter((action) => action.initiator === loadDeviceParameterValues.type),
      map(() => showLoading({ text: 'loading device parameter values' })),
    ),
  );

  hideLoadingOnDeviceParameterValuesLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDeviceParameters),
      filter((action) => action.initiator === loadDeviceParameterValues.type),
      map(() => hideLoading({})),
    ),
  );

  showLoadingOnGetDeviceStackImageRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceFileRequest),
      filter((action) => action.initiator === addDevice.type && action.filename === 'stack_image.svg.zip'),
      map(() => showLoading({ text: 'loading device image' })),
    ),
  );

  toastrOnSystemEventErrorOrWarning$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(systemEvent),
        tap(({ systemEvent: { error, warning } }) => {
          if (error && error.message) {
            this.toastr.error(error.message, 'System Event', { disableTimeOut: true });
          } else if (warning) {
            if (warning.message) {
              this.toastr.warning(warning.message, 'System Event', { disableTimeOut: true });
            } else if (warning.code === MotionMasterMessage.Status.SystemEvent.Warning.Code.DROPPING_CONNECTION) {
              this.toastr.warning('Motion Master has dropped the connection.', 'System Event', {
                disableTimeOut: true,
              });
            }
          }
        }),
      ),
    { dispatch: false },
  );

  showLoadingOnLoadDeviceConfigurationFromFileRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceConfigurationFromFileRequest),
      map(() => showLoading({ text: 'loading configuration from file' })),
    ),
  );

  hideLoadingOnLoadDeviceConfigurationFromFileEnded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceConfigurationFromFileStatus),
      filter((action) => procedureStatusEnded(action.procedureStatus)),
      map(() => hideLoading({})),
    ),
  );

  showLoadingOnFactoryResetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(factoryResetRequest),
      map(() => showLoading({ text: 'factory reset in progress' })),
    ),
  );

  hideLoadingOnFactoryResetStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(factoryResetStatus),
      filter((action) => procedureStatusEnded(action.procedureStatus)),
      map(() => hideLoading({})),
    ),
  );

  showLoadingOnStartCirculoEncoderConfigurationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCirculoEncoderConfigurationRequest),
      map(() => showLoading({ text: 'configuring the integrated encoder' })),
    ),
  );

  hideLoadingOnCirculoEncoderConfigurationProcedureCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(circuloEncoderConfigurationProcedureCompleted),
      map(() => hideLoading({})),
    ),
  );

  /**
   * Configuring the integrated encoder is three steps behind one button: the procedure on the drive, writing
   * the configuration backup file, and reading register 0x11 back to verify it. Only the first step reports
   * itself, through the status toast, and the overlay stays up for the two that follow -- which reads as a
   * hang when its text still says the procedure is running. Say what is actually happening instead.
   */
  showLoadingWhileSavingCirculoEncoderConfigurationBinFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCirculoEncoderConfigurationStatus),
      filter((action) => circuloEncoderConfigurationCompleted(action.circuloEncoderConfiguration)),
      map(() => showLoading({ text: 'saving the integrated encoder configuration on the device' })),
    ),
  );

  showLoadingWhileVerifyingCirculoEncoderConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggerCirculoRegisterReadAfterWritingEncoderConfigBin),
      filter((action) => action.flow === 'configuration'),
      map(() => showLoading({ text: 'verifying the integrated encoder configuration' })),
    ),
  );

  hideLoadingOnCirculoEncoderConfigurationEnded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCirculoEncoderConfigurationStatus),
      filter(
        (action) =>
          circuloEncoderConfigurationEnded(action.circuloEncoderConfiguration) &&
          !circuloEncoderConfigurationCompleted(action.circuloEncoderConfiguration),
      ),
      map(() => hideLoading({})),
    ),
  );

  hideLoadingOnEncoderWriteFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(writeCirculoEncoderConfigFailed),
      map(() => hideLoading({})),
    ),
  );

  //
  // Save problem report.
  //

  dispatchSaveProblemReportStatusOnRequest$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveProblemReportRequest),
        tap(() => {
          this.toastr.info('Collecting and saving the problem report files started.', 'Save Problem Report', {
            disableTimeOut: true,
          });
        }),
      ),
    { dispatch: false },
  );

  dispatchUpdateProblemReportSavingRunningOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveProblemReportRequest),
      map(() => updateProblemReportSavingRunning({ running: true })),
    ),
  );

  handleSaveProblemReportRequest$ = createEffect(() => {
    const filesList = [
      '.hardware_description',
      '.safety_parameters_report',
      '.variant',
      'cogging_torque.bin',
      'config.csv',
      'logging_curr.log',
      'logging_prev.log',
      'plant_model.csv',
      'stack_info.json',
      'ui.config.json',
    ];

    return this.actions$.pipe(
      ofType(saveProblemReportRequest),
      exhaustMap((action) =>
        zip(
          this.store$.select(selectGlobalAppVersion),
          this.store$.select(selectGlobalSystemInformation),
          this.store$.select(selectGlobalOblacDrivesUpdateServiceVersion),
          this.store$.select(selectMatchingRelease),
          this.cn.client.request.getSystemLog(20000),
          this.store$.select<Device[]>(selectAllDevices).pipe(first()),
          from(this.electronService.readStoredLog()),
        ).pipe(
          first(),
          timeout(action.timeout),
          exhaustMap(
            ([appVersion, systemInformation, odusVersion, matchingRelease, systemLog, devices, electronLog]) => {
              const jz = new JSZip();

              // Add general files
              jz.file(
                'versions.txt',
                [
                  `UI: ${appVersion}`,
                  `MM: ${systemInformation.version}`,
                  `OD: ${matchingRelease?.version || 'obsolete'}`,
                  `ODUS: ${odusVersion}`,
                ].join('\n'),
              );
              jz.file('system-log.txt', systemLog.content);
              jz.file('system-proc.txt', systemLog.runEnv);
              jz.file('electron-log.txt', electronLog);

              // Handle case where no devices exist
              if (devices.length === 0) {
                return from(
                  jz.generateAsync({ type: 'blob' }).then((blob: Blob) => {
                    const dateTimeString = makeLocalDateTimeStringForFileSave();
                    saveAs(blob, `oblac-drives-problem-report-${dateTimeString}.zip`);
                  }),
                ).pipe(
                  map(() =>
                    saveProblemReportStatus({
                      procedureStatus: {
                        success: { message: 'The problem report files are collected and saving is initiated.' },
                      },
                      suppressToastr: false,
                    }),
                  ),
                );
              }

              return zip(
                devices.map(({ serialNumber, deviceAddress }) =>
                  zip(
                    of(serialNumber),
                    this.cn.client.request
                      .getDeviceParameters({
                        deviceSerialNumber: serialNumber,
                        loadFromCache: false,
                        sendProgress: false,
                        extendWithEsi: true,
                      })
                      .pipe(
                        first(),
                        map(({ parameters }) => {
                          const parametersCsv = parameters?.length
                            ? [
                                'Index,Subindex,Value,Name',
                                ...parameters.map((p) => {
                                  const typeValue = p.typeValue ?? 'uintValue';

                                  let value: string | number;

                                  if (typeValue === 'rawValue') {
                                    value =
                                      p.value instanceof Uint8Array
                                        ? Array.from(p.value).join(',')
                                        : p.value.toString();
                                  } else {
                                    value = p.value as string | number;
                                  }

                                  const parsedValue = parseParameterValue(typeValue, value);

                                  return `0x${p.index.toString(16)},${p.subindex},${
                                    typeValue === 'rawValue' ? `"${parsedValue}"` : parsedValue
                                  },${p.name}`;
                                }),
                              ].join('\n')
                            : null;

                          // Parameters the ESI file does not describe fall back to permissive metadata - no unit,
                          // documentation or range - which makes an ESI that does not match the installed firmware
                          // easy to miss. List them so the missing definitions do not stay in the console only.
                          const parametersWithEsiExtensionError = parameters?.filter((p) => p.esiExtensionError) ?? [];
                          const esiExtensionErrors = parametersWithEsiExtensionError.length
                            ? [
                                `${parametersWithEsiExtensionError.length} of ${parameters.length} parameters could not be extended with the metadata from the ESI file.`,
                                '',
                                'Index,Subindex,Name,Reason',
                                ...parametersWithEsiExtensionError.map(
                                  (p) =>
                                    `0x${p.index.toString(16)},${p.subindex},${p.name},"${p.esiExtensionError.message}"`,
                                ),
                              ].join('\n')
                            : null;

                          return { esiExtensionErrors, parametersCsv };
                        }),
                        catchError(() => of(null)),
                      ),
                    zip(
                      filesList.map((name) =>
                        this.cn.client.request
                          .getDeviceFile({ deviceAddress, name }, 5000)
                          .pipe(mockDeviceFileOnError(deviceAddress, name)),
                      ),
                    ),
                    this.cn.client.request
                      .getDeviceFileList({ deviceAddress }, 5000)
                      .pipe(catchError((error) => of({ error: { message: error.message } } as DeviceFileListStatus))),
                  ),
                ),
              ).pipe(
                tap((deviceData) => {
                  deviceData.forEach(([serialNumber, deviceParameters, files, fileListStatus]) => {
                    const deviceFolder = jz.folder(serialNumber);
                    if (deviceParameters?.parametersCsv) {
                      deviceFolder.file('parameters.csv', deviceParameters.parametersCsv);
                    }
                    if (deviceParameters?.esiExtensionErrors) {
                      deviceFolder.file('esi-extension-errors.txt', deviceParameters.esiExtensionErrors);
                    }

                    files.forEach((file) => {
                      const deviceFile = file as DeviceFileStatus;
                      if (deviceFile.error) {
                        deviceFolder.file(deviceFile.name, deviceFile.error.message);
                      } else if (deviceFile.content) {
                        const binaryContent = new Uint8Array(deviceFile.content);
                        const data = new Blob([binaryContent], { type: 'application/octet-stream' });
                        deviceFolder.file(deviceFile.name, data);
                      }
                    });

                    if (fileListStatus?.error) {
                      deviceFolder.file('file-list.txt', fileListStatus.error.message);
                    } else if (fileListStatus?.fileList?.files.length) {
                      deviceFolder.file('file-list.txt', fileListStatus.fileList.files.join('\n'));
                    } else {
                      deviceFolder.file('file-list.txt', '');
                    }
                  });

                  // Generate and download the ZIP
                  jz.generateAsync({ type: 'blob' }).then((blob: Blob) => {
                    const dateTimeString = makeLocalDateTimeStringForFileSave();
                    saveAs(blob, `oblac-drives-problem-report-${dateTimeString}.zip`);
                  });
                }),
                map(() =>
                  saveProblemReportStatus({
                    procedureStatus: {
                      success: { message: 'The problem report files are collected and saving is initiated.' },
                    },
                    suppressToastr: false,
                  }),
                ),
              );
            },
          ),
        ),
      ),
      catchError((error) =>
        of(
          saveProblemReportStatus({
            procedureStatus: { error: { message: error.message } },
            suppressToastr: false,
          }),
        ),
      ),
    );
  });

  handleSaveProblemReportStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveProblemReportStatus),
      tap((action) => {
        this.toastr.clear();
        this.globalService.toastrStatusMessage(action.procedureStatus, 'Save Problem Report', action.suppressToastr);
      }),
      map(({ procedureStatus }) =>
        updateProblemReportSavingRunning({ running: procedureStatus.success || procedureStatus.error ? false : true }),
      ),
    ),
  );

  handleUpdateProblemReportSavingRunning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProblemReportSavingRunning),
      map((action) => updateGlobalState({ state: { problemReportSavingRunning: action.running } })),
    ),
  );

  dispatchBulkSaveConfigurationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkSaveConfigurationFilesRequest),
      map((action) =>
        bulkSaveConfigurationFilesStatus({
          ...action,
          procedureStatus: {
            begin: {
              message: 'Collecting the problem report files and saving.',
            },
          },
        }),
      ),
    ),
  );

  handleBulkSaveConfigurationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(bulkSaveConfigurationFilesRequest),
      exhaustMap(({ devices, timeout }) =>
        forkJoin(
          devices.map((device) =>
            this.cn.client.request
              .getDeviceFile({ deviceAddress: device.deviceAddress, name: 'config.csv' }, timeout)
              .pipe(
                map((status) => ({
                  device,
                  content: status.content ?? null,
                  error: status.error ?? null,
                })),
                catchError((error) =>
                  of({
                    device,
                    content: null,
                    error: { message: error?.message ?? 'Unknown error' },
                  }),
                ),
              ),
          ),
        ).pipe(
          // Split responses array into valid and invalid responses arrays
          map((items) =>
            items.reduce(
              (acc, res) => {
                !res.error ? acc[0].push(res) : acc[1].push(res);
                return acc;
              },
              [[], []],
            ),
          ),
          tap(([validResponses]) => {
            if (validResponses.length) {
              const zipFile = new JSZip();
              validResponses.forEach(({ device, content }) => {
                const fileName = `${device.serialNumber}.config.csv`;
                zipFile.file(fileName, new Uint8Array(content!));
              });

              zipFile.generateAsync({ type: 'blob' }).then((blob) => {
                const dateTimeString = makeLocalDateTimeStringForFileSave();
                saveAs(blob, `devices-config-${dateTimeString}.zip`);
              });
            }
          }),
          map(([validResponses, invalidResponses]) => {
            let message = 'Successfully downloaded configuration files.';

            if (invalidResponses.length) {
              if (!validResponses.length) {
                message = 'Failed to retrieve configuration files.';
              } else {
                const totalResponsesCount = validResponses.length + invalidResponses.length;
                const devicesIds = invalidResponses.map((r) => r.device.serialNumber);
                message = `Downloaded ${validResponses.length}/${totalResponsesCount} configuration files successfully. Failed for: ${devicesIds.join(', ')}.`;
              }
            }

            return bulkSaveConfigurationFilesStatus({
              procedureStatus: {
                error: invalidResponses.length ? { message } : null,
                success: !invalidResponses.length ? { message } : null,
              },
            });
          }),
          catchError((error) =>
            of(
              bulkSaveConfigurationFilesStatus({
                procedureStatus: {
                  error: { message: error?.message ?? 'Unexpected error occurred.' },
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  handleBulkSaveConfigurationFilesStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(bulkSaveConfigurationFilesStatus),
        tap((action) =>
          this.globalService.toastrStatusMessage(
            action.procedureStatus,
            'Bulk Save Configuration',
            action.suppressToastr,
          ),
        ),
      ),
    { dispatch: false },
  );

  handleOpenParameterDocumentationModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openParameterDocumentationModal),
        tap((action) => {
          const { parameter, dismissAll } = action;
          this.modalService.openParameterDocumentationModal(parameter, dismissAll);
        }),
      ),
    { dispatch: false },
  );

  handleOpenHomingMethodDescriptionModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openHomingMethodDescriptionModal),
        tap((action) => {
          const { homingMethodId, dismissAll } = action;
          this.modalService.openHomingMethodDescriptionModal(homingMethodId, dismissAll);
        }),
      ),
    { dispatch: false },
  );

  handleOpenQuickOpenModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openQuickOpenModal),
        tap((action) => {
          const { device, searchTerm, dismissAll } = action;
          this.modalService.openQuickOpenModal(device, searchTerm, dismissAll);
        }),
      ),
    { dispatch: false },
  );

  handleGetEncoderConfigurationUsage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getEncoderConfigurationUsageRequest),
      mergeMap(() => from(fetchEncoderConfigurationsUsage()).pipe(catchError(() => of(null)))),
      filter((response) => response != null),
      map((response) => getEncoderConfigurationUsageStatus({ encoderConfigurationUsage: response })),
    ),
  );
}
